<template>
  <div class="action-expandable">
    <button class="action-button" v-title.bottom="title" @click="toggleSelector">
      <i class="material-icons">{{ icon }}</i>
      <span v-if="selected" class="selected-text">
        <template v-if="showPosition">{{ sortedOptions.findIndex(opt => opt._id === selected._id) + 1 }} - </template>
        {{ selected[label] }}
      </span>
    </button>
    <div class="selector-content" ref="selectorContent" v-show="showSelector">
      <div class="selector-header">
        <span>{{ title }}</span>
      </div>
      <div class="search-container">
        <div class="search-input">
          <i class="material-icons">search</i>
          <input
            type="text"
            v-model="searchQuery"
            :placeholder="$t('components.select_button.search')"
            @click.stop
          >
        </div>
      </div>
      <div class="selector-options">
        <template v-if="sortedOptions.length > 0">
          <div
            class="selector-option"
            v-for="(option, index) in sortedOptions"
            :key="option._id"
            @click="selectOption(option)"
            :class="{ selected: option._id === valueId }"
          >
            <span v-if="showPosition" class="position-number">{{ index + 1 }} - </span>
            <i class="material-icons">{{ option._id === valueId ? 'check_circle' : icon }}</i>
            <span>{{ option[label] }}</span>
          </div>
        </template>
        <div v-else class="no-results">
          {{ $t('components.select_button.no_results') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import gsap from 'gsap'

export default {
  name: 'SelectButton',
  props: {
    title: {
      type: String,
      required: false,
      default: ''
    },
    icon: {
      type: String,
      required: false,
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
    label: {
      type: String,
      required: false,
      default: 'label'
    },
    valueId: {
      type: String,
      required: false,
      default: ''
    },
    autoWidth: {
      type: Boolean,
      required: false,
      default: false
    },
    showTitle: {
      type: Boolean,
      required: false,
      default: true
    },
    enableSearch: {
      type: Boolean,
      required: false,
      default: false
    },
    selectedFirst: {
      type: Boolean,
      required: false,
      default: true
    },
    showPosition: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      showSelector: false,
      searchQuery: '',
      animation: null
    }
  },
  computed: {
    selected () {
      if (!this.valueId || !this.options.length) return null
      return this.options.find(opt => String(opt._id) === String(this.valueId))
    },
    filteredOptions () {
      if (!this.searchQuery) return this.options
      const query = this.searchQuery.toLowerCase()
      return this.options.filter(option =>
        option[this.label].toLowerCase().includes(query)
      )
    },
    sortedOptions () {
      if (!this.selectedFirst) return this.filteredOptions

      return [...this.filteredOptions].sort((a, b) => {
        if (a._id === this.valueId) return -1
        if (b._id === this.valueId) return 1
        return 0
      })
    }
  },
  methods: {
    toggleSelector () {
      if (this.animation) this.animation.kill()
      if (!this.showSelector) {
        this.showSelector = true
        this.$nextTick(() => {
          const content = this.$refs.selectorContent
          const height = content.scrollHeight
          this.animation = gsap.fromTo(content,
            {
              height: 0,
              opacity: 0,
              transformOrigin: 'top'
            },
            {
              height,
              opacity: 1,
              duration: 0.3,
              ease: 'power2.out'
            }
          )
        })
      } else {
        this.animation = gsap.to(this.$refs.selectorContent, {
          height: 0,
          opacity: 0,
          duration: 0.3,
          ease: 'power2.in',
          onComplete: () => {
            this.showSelector = false
            this.searchQuery = ''
          }
        })
      }
      this.$emit('toggle', !this.showSelector)
    },
    selectOption (option) {
      if (option._id === this.valueId) return

      this.$emit('input', option)
      this.$emit('select', option)
      this.$emit('toggle', false)
      this.toggleSelector()
    },
    closeSelector () {
      if (this.showSelector) {
        this.toggleSelector()
      }
    }
  },
  mounted () {
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target) && this.showSelector) {
        this.closeSelector()
      }
    })
  },
  beforeDestroy () {
    if (this.animation) {
      this.animation.kill()
    }
    document.removeEventListener('click', this.closeSelector)
  }
}
</script>

<style lang="scss" scoped>
.action-expandable {
  position: relative;
  width: v-bind('autoWidth ? "100%" : "auto"');
}

.action-button {
  width: v-bind('autoWidth ? "100%" : "auto"');
  background: none;
  border: none;
  padding: 0.8vh;
  cursor: pointer;
  border-radius: 8px;
  color: #666;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.8vh;

  .selected-text {
    font-size: 1.8vh;
    max-width: v-bind('autoWidth ? "none" : "120px"');
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: color-mix(in srgb, var(--accent-color) 10%, transparent);
    color: var(--accent-color);
  }

  i {
    font-size: 3vh;
  }
}

.selector-content {
  position: absolute;
  top: 100%;
  right: v-bind('autoWidth ? "0" : "0"');
  left: v-bind('autoWidth ? "0" : "auto"');
  width: v-bind('autoWidth ? "100%" : "250px"');
  height: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-top: 0.8vh;
  z-index: 1000;

  .selector-header {
    padding: 1.2vh 1.6vh;
    border-bottom: 1px solid #e0e0e0;
    font-weight: 600;
    color: #333;
    font-size: 1.6vh;
    text-align: center;
    display: v-bind('showTitle ? "block" : "none"');
  }

  .search-container {
    padding: 0.8vh;
    border-bottom: 1px solid #e0e0e0;
    display: v-bind('enableSearch ? "block" : "none"');

    .search-input {
      display: flex;
      align-items: center;
      gap: 0.8vh;
      background-color: #f5f5f5;
      border-radius: 4px;
      padding: 0.6vh 0.8vh;

      i {
        font-size: 1.8vh;
        color: #666;
      }

      input {
        border: none;
        background: none;
        outline: none;
        width: 100%;
        font-size: 1.6vh;
        color: #333;

        &::placeholder {
          color: #999;
        }
      }
    }
  }

  .selector-options {
    max-height: 30vh;
    overflow-y: auto;

    .selector-option {
      display: flex;
      align-items: center;
      gap: 0.8vh;
      padding: 1vh 1.6vh;
      cursor: pointer;
      transition: all 0.2s ease;

      .position-number {
        font-size: 1.6vh;
        color: #999;
        min-width: 2.4vh;
      }

      i {
        font-size: 1.8vh;
        color: #999;
      }

      span {
        font-size: 1.6vh;
        color: #666;
      }

      &:hover {
        background: color-mix(in srgb, var(--accent-color) 5%, transparent);
        i, span {
          color: var(--accent-color);
        }
      }

      &.selected {
        background: color-mix(in srgb, var(--accent-color) 10%, transparent);
        border-bottom: 1px solid #e0e0e0;
        i, span {
          color: var(--accent-color);
        }
      }
    }
  }

  .no-results {
    padding: 1.6vh;
    text-align: center;
    color: #666;
    font-size: 1.6vh;
  }
}
</style>
